/**
 * Schema utility functions for JSON-LD structured data
 */
import appConfig from "@/configs/app-config";
import { getLocalizedPath } from "@/utils/pathUtils";

const LANDING_HOTEL_LIST_SCHEMA_MAX_ITEMS = 20;

/**
 * Generate JSON-LD BreadcrumbList schema from breadcrumb items
 */
/**
 * Landing sayfaları için ItemList (Hotel) JSON-LD schema üretir (server veya client'ta kullanılabilir).
 * Maksimum 20 otel dahil edilir.
 *
 * @param {Object} params
 * @param {Array<Object>} params.hotels - mapLandingHotelItem ile map edilmiş otel listesi
 * @param {string} [params.baseUrl] - Site base URL. Varsayılan: appConfig.SITE_URL
 * @param {string} [params.priceRangeText] - priceRange metni. Varsayılan: "En uygun fiyat ve taksit imkanıyla"
 * @param {string} [params.addressCountry] - Adres ülke. Varsayılan: "Türkiye"
 * @returns {Object|null} Schema.org ItemList JSON-LD objesi veya otel yoksa null
 */
export function generateLandingHotelListSchema({
  hotels = [],
  baseUrl = appConfig.SITE_URL,
  priceRangeText = "En uygun fiyat ve taksit imkanıyla",
  addressCountry = "Türkiye",
}) {
  if (!hotels || hotels.length === 0) return null;

  const limitedHotels = hotels.slice(0, LANDING_HOTEL_LIST_SCHEMA_MAX_ITEMS);

  const itemListElement = limitedHotels.map((hotel, index) => {
    const url = hotel?.link
      ? hotel.link.startsWith("http")
        ? hotel.link
        : `${baseUrl}${hotel.link}`
      : "";

    const imageUrl =
      Array.isArray(hotel?.imageUrl) && hotel.imageUrl.length > 0
        ? hotel.imageUrl[0]
        : Array.isArray(hotel?.Images) && hotel.Images.length > 0
          ? hotel.Images[0]
          : null;
    const image =
      imageUrl && imageUrl.startsWith("http")
        ? imageUrl
        : imageUrl
          ? `${baseUrl}${imageUrl}`
          : undefined;

    const address = {
      "@type": "PostalAddress",
      addressLocality: hotel?.location || "",
      addressCountry,
    };
    if (hotel?.streetAddress) address.streetAddress = hotel.streetAddress;

    const item = {
      "@type": "Hotel",
      name: hotel?.name || "Otel",
      ...(url && { url }),
      ...(image && { image }),
      address,
      priceRange: priceRangeText,
    };
    if (hotel?.telephone) item.telephone = hotel.telephone;
    if (
      typeof hotel?.rating === "number" &&
      hotel.rating > 0 &&
      typeof hotel?.reviewCount === "number"
    ) {
      item.aggregateRating = {
        "@type": "AggregateRating",
        bestRating: "10.0",
        worstRating: 1,
        ratingValue: String(hotel.rating),
        reviewCount: String(hotel.reviewCount),
      };
    }
    return {
      "@type": "ListItem",
      position: index + 1,
      item,
    };
  });

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement,
  };
}

export function generateBreadcrumbSchema(
  breadcrumbItems,
  currentPath = "",
  locale = "tr",
) {
  if (
    !breadcrumbItems ||
    !Array.isArray(breadcrumbItems) ||
    breadcrumbItems.length === 0
  ) {
    return null;
  }

  const baseUrl = appConfig.SITE_URL;
  const itemListElement = breadcrumbItems.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@id": item.href
        ? `${baseUrl}${item.href}`
        : `${baseUrl}${getLocalizedPath(currentPath, locale)}`,
      name: item.label,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: itemListElement,
  };
}

/**
 * Generate JSON-LD FAQPage schema from FAQ items.
 * Supports API-style (Title, Description), dynamic (Question, Answer) and camelCase (title, question, etc.).
 */
export function generateFaqSchema(faqItems) {
  if (!faqItems || !Array.isArray(faqItems) || faqItems.length === 0) {
    return null;
  }

  const mainEntity = faqItems
    .map((item) => {
      const name =
        item.title ?? item.question ?? item.Title ?? item.Question ?? "";
      const text =
        item.html ??
        item.answer ??
        item.description ??
        item.Description ??
        item.Answer ??
        "";
      return {
        "@type": "Question",
        name: String(name || "").trim() || undefined,
        acceptedAnswer: {
          "@type": "Answer",
          text: String(text || "").trim() || undefined,
        },
      };
    })
    .filter((q) => q.name && q.acceptedAnswer?.text);

  if (mainEntity.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

/**
 * Generate JSON-LD WebSite schema
 * @param {Function} t - Translation function from next-intl
 * @returns {Object} WebSite schema object
 */
export function generateWebsiteSchema(t) {
  const baseUrl = appConfig.SITE_URL;

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Turna.com",
    url: `${baseUrl}/`,
  };
}

/**
 * Generate JSON-LD Organization schema
 * @param {Function} t - Translation function from next-intl
 * @returns {Object} Organization schema object
 */
export function generateOrganizationSchema(t) {
  const baseUrl = appConfig.SITE_URL;

  return {
    "@context": "http://schema.org",
    "@type": "Organization",
    name: "Turna.com",
    description: t("common.organizationSchemaDescription"),
    url: baseUrl,
    logo: `${baseUrl}/icon.png`,
    slogan: t("common.organizationSchemaSlogan"),
    legalName: "TRIPENIA BİLİŞİM TURİZM SEYAHAT ACENTASI",
    foundingDate: "2014-11-01",
    numberOfEmployees: {
      "@type": "QuantitativeValue",
      value: 110,
    },
    award: [
      t("common.organizationSchemaAward0"),
      t("common.organizationSchemaAward1"),
      t("common.organizationSchemaAward2"),
    ],
    address: {
      "@type": "PostalAddress",
      addressCountry: "TR",
      addressLocality: "Istanbul, Turkey",
      postalCode: "34420",
      streetAddress:
        "Cifte Havuzlar Mahallesi YTU Yildiz Teknopark Davutpasa Kampusu C1 Blok No:310",
    },
    knowsAbout: [
      t("common.organizationSchemaKnowsAbout0"),
      t("common.organizationSchemaKnowsAbout1"),
      t("common.organizationSchemaKnowsAbout2"),
      t("common.organizationSchemaKnowsAbout3"),
      t("common.organizationSchemaKnowsAbout4"),
    ],
    makesOffer: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Uçak Bileti",
          description: t(
            "common.organizationSchemaMakesOfferFlightDescription",
          ),
          provider: {
            "@type": "Organization",
            name: "Turna.com",
          },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Otobüs Bileti",
          description: t("common.organizationSchemaMakesOfferBusDescription"),
          provider: {
            "@type": "Organization",
            name: "Turna.com",
          },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Feribot Bileti",
          description: t("common.organizationSchemaMakesOfferFerryDescription"),
          provider: {
            "@type": "Organization",
            name: "Turna.com",
          },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Otel Rezervasyonu",
          description: t("common.organizationSchemaMakesOfferHotelDescription"),
          provider: {
            "@type": "Organization",
            name: "Turna.com",
          },
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Araç Kiralama",
          description: t("common.organizationSchemaMakesOfferCarDescription"),
          provider: {
            "@type": "Organization",
            name: "Turna.com",
          },
        },
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        email: "info@turna.com",
        telephone: "+908502226600",
        contactType: "customer service",
        contactOption: "TollFree",
        faxNumber: "+902126036178",
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      },
    ],
    memberOf: [
      {
        "@type": "Organization",
        name: "TÜRSAB",
        url: "https://www.tursab.org.tr/",
      },
      {
        "@type": "Organization",
        name: "IATA",
        url: "https://www.iata.org/",
      },
    ],
    sameAs: [
      "https://www.facebook.com/turnacom",
      "https://www.instagram.com/turna_com/",
      "https://twitter.com/turna_com",
      "https://www.linkedin.com/company/turna.com",
      "https://www.youtube.com/channel/UCNc36KT-tVbglbzwvwRjy7w/videos",
      "https://www.tiktok.com/@turnacom",
      "https://www.crunchbase.com/organization/turna",
    ],
  };
}
